import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Star, Hash, User, Calendar, MapPin, Clock, 
  Sparkles, ChevronRight, Crown, Lock, Check,
  Sun, Moon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PersonalDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/user/dashboard`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setDashboard(data);
        }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [isAuthenticated]);

  // Don't render if not authenticated
  if (!isAuthenticated || loading) {
    return null;
  }

  // Don't render if no birth data
  if (!dashboard?.user?.has_birth_data) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Bienvenue, {user?.first_name || 'Voyageur'} !</h3>
                <p className="text-sm text-gray-600">Complétez votre profil pour débloquer votre tableau de bord personnalisé</p>
              </div>
            </div>
            <Link to="/profile">
              <Button className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white">
                <Sparkles className="h-4 w-4 mr-2" />
                Compléter mon profil
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    );
  }

  const { astrology, numerology, premium_status } = dashboard;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
      data-testid="personal-dashboard"
    >
      {/* Dashboard Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <h2 className="text-lg font-bold text-gray-900">Mon Tableau de Bord</h2>
        </div>
        <Link 
          to="/profile" 
          className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
        >
          Voir mon profil
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Dashboard Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        
        {/* Astrology Card */}
        {astrology && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-2xl border border-sky-200 p-5 hover:shadow-lg hover:shadow-sky-100 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center">
                  <Sun className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-sky-600 font-medium">ASTROZODIAC</p>
                  <h3 className="font-bold text-gray-900">Signe Solaire</h3>
                </div>
              </div>
              <Link to="/astrozodiac">
                <ChevronRight className="h-5 w-5 text-sky-400 hover:text-sky-600" />
              </Link>
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              <span className="text-4xl">{astrology.sun_sign.symbol}</span>
              <div>
                <p className="text-xl font-bold text-gray-900">{astrology.sun_sign.name}</p>
                <p className="text-xs text-gray-500">
                  {new Date(astrology.birth_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {astrology.has_birth_time ? (
                <span className="px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded-full flex items-center gap-1">
                  <Check className="h-3 w-3" /> Heure connue
                </span>
              ) : (
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Heure manquante
                </span>
              )}
              {astrology.has_birth_city ? (
                <span className="px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded-full flex items-center gap-1">
                  <Check className="h-3 w-3" /> Ville connue
                </span>
              ) : (
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Ville manquante
                </span>
              )}
            </div>

            <Link 
              to="/astrozodiac/big-three"
              className="mt-4 block w-full text-center py-2 bg-sky-100 hover:bg-sky-200 text-sky-700 rounded-xl text-sm font-medium transition-colors"
            >
              Voir mes 3 Piliers →
            </Link>
          </motion.div>
        )}

        {/* Numerology Card */}
        {numerology && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 p-5 hover:shadow-lg hover:shadow-emerald-100 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <Hash className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">NUMEROLOGIA</p>
                  <h3 className="font-bold text-gray-900">Chemin de Vie</h3>
                </div>
              </div>
              <Link to="/numerologia">
                <ChevronRight className="h-5 w-5 text-emerald-400 hover:text-emerald-600" />
              </Link>
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                numerology.life_path.is_master 
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                  : 'bg-gradient-to-br from-emerald-400 to-teal-500'
              }`}>
                <span className="text-2xl font-bold text-white">{numerology.life_path.number}</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{numerology.life_path.meaning}</p>
                {numerology.life_path.is_master && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full mt-1">
                    <Crown className="h-3 w-3" /> Nombre Maître
                  </span>
                )}
              </div>
            </div>

            <Link 
              to="/numerologia/theme"
              className="mt-4 block w-full text-center py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-xl text-sm font-medium transition-colors"
            >
              Voir mon thème complet →
            </Link>
          </motion.div>
        )}

        {/* Premium Status Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className={`rounded-2xl border p-5 transition-all ${
            premium_status 
              ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 hover:shadow-lg hover:shadow-amber-100' 
              : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'
          }`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                premium_status 
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                  : 'bg-gray-300'
              }`}>
                <Crown className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">STATUT</p>
                <h3 className="font-bold text-gray-900">
                  {premium_status ? 'Premium Actif' : 'Compte Gratuit'}
                </h3>
              </div>
            </div>
          </div>
          
          {premium_status ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="h-4 w-4 text-emerald-500" />
                <span>Accès à toutes les applications</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="h-4 w-4 text-emerald-500" />
                <span>Interprétations IA illimitées</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="h-4 w-4 text-emerald-500" />
                <span>Historique complet</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Lock className="h-4 w-4" />
                <span>Modules premium verrouillés</span>
              </div>
              <Link 
                to="/premium"
                className="block w-full text-center py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-xl text-sm font-medium transition-colors"
              >
                <Sparkles className="h-4 w-4 inline mr-2" />
                Passer Premium
              </Link>
            </div>
          )}
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link 
          to="/mysteriae/draw" 
          className="flex items-center gap-2 p-3 bg-purple-50 hover:bg-purple-100 rounded-xl border border-purple-200 transition-colors"
        >
          <Moon className="h-5 w-5 text-purple-500" />
          <span className="text-sm font-medium text-gray-700">Tirage Tarot</span>
        </Link>
        <Link 
          to="/astrozodiac/transits" 
          className="flex items-center gap-2 p-3 bg-sky-50 hover:bg-sky-100 rounded-xl border border-sky-200 transition-colors"
        >
          <Star className="h-5 w-5 text-sky-500" />
          <span className="text-sm font-medium text-gray-700">Transits du jour</span>
        </Link>
        <Link 
          to="/numerologia/cycles" 
          className="flex items-center gap-2 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl border border-emerald-200 transition-colors"
        >
          <Hash className="h-5 w-5 text-emerald-500" />
          <span className="text-sm font-medium text-gray-700">Cycles perso</span>
        </Link>
        <Link 
          to="/home" 
          className="flex items-center gap-2 p-3 bg-amber-50 hover:bg-amber-100 rounded-xl border border-amber-200 transition-colors"
        >
          <Sparkles className="h-5 w-5 text-amber-500" />
          <span className="text-sm font-medium text-gray-700">Scan de paume</span>
        </Link>
      </div>
    </motion.div>
  );
};

export default PersonalDashboard;
